import React from 'react';
import {
    Box,
    Button,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Card,
    CardContent,
    Chip,
    useTheme,
    useMediaQuery,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {Badge, Payments, History, MoreHoriz, Engineering, WorkHistory} from '@mui/icons-material';

interface RollerVeUcretlerTabProps {
    roller: any[];
    activeRol: any;
    ucretler: any[];
    onNewRol: () => void;
    onEditRol: (row: any) => void;
    onDeleteRol: (id: number) => void;
    onRolClick: (row: any) => void;
    onNewUcret: () => void;
    onEditUcret: (row: any) => void;
    onDeleteUcret: (id: number) => void;
}

const RollerVeUcretlerTab: React.FC<RollerVeUcretlerTabProps> = ({
                                                                     roller,
                                                                     activeRol,
                                                                     ucretler,
                                                                     onNewRol,
                                                                     onEditRol,
                                                                     onDeleteRol,
                                                                     onRolClick,
                                                                     onNewUcret,
                                                                     onEditUcret,
                                                                     onDeleteUcret,
                                                                 }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Stack direction="column" spacing={isMobile ? 3 : 5}>
            <Box>
                <Box sx={{mb: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Badge color="primary" />
                        <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Rol Tanımları</Typography>
                    </Stack>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: '12px', px: 3, fontWeight: 800 }}
                        startIcon={<AddIcon/>}
                        onClick={onNewRol}
                    >
                        Yeni Rol Ekle
                    </Button>
                </Box>
                
                {isMobile ? (
                    <Stack spacing={1.5}>
                        {roller.map((row: any) => (
                            <Card 
                                key={row.id} 
                                onClick={() => onRolClick(row)}
                                sx={{ 
                                    borderRadius: '12px', 
                                    border: '2px solid',
                                    borderColor: activeRol?.id === row.id ? 'primary.main' : 'rgba(0,0,0,0.05)',
                                    bgcolor: activeRol?.id === row.id ? 'primary.light' : 'background.paper',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1" fontWeight={800}>{row.rolAdi}</Typography>
                                        <Chip label={row.rolTipi} size="small" variant="outlined" sx={{ fontWeight: 700, borderRadius: '6px' }} />
                                    </Box>
                                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEditRol(row); }} sx={{ bgcolor: 'white' }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDeleteRol(row.id); }} sx={{ bgcolor: 'white' }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 800 }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>Rol Adı</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>Tip</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800 }}>İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {roller.map((row: any) => (
                                    <TableRow
                                        key={row.id}
                                        onClick={() => onRolClick(row)}
                                        selected={activeRol?.id === row.id}
                                        sx={{
                                            cursor: 'pointer',
                                            '&.Mui-selected': { bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.light' } },
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.rolAdi}</TableCell>
                                        <TableCell><Chip label={row.rolTipi} size="small" sx={{ fontWeight: 700, borderRadius: '6px' }} /></TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEditRol(row); }}>
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => { e.stopPropagation(); onDeleteRol(row.id); }}
                                            >
                                                <DeleteIcon fontSize="small"/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
            
            {activeRol && (
                <Box>
                    <Divider sx={{ mb: 4 }} />
                    <Box sx={{ mb: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <WorkHistory color="primary" />
                            <Typography variant="h6" fontWeight={800} color="primary.dark">
                                [{activeRol.rolAdi}] Ücret Geçmişi
                            </Typography>
                        </Stack>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{ borderRadius: '12px', px: 3, fontWeight: 800, borderWidth: '2px', '&:hover': { borderWidth: '2px' } }}
                            startIcon={<AddIcon/>}
                            onClick={onNewUcret}
                        >
                            Yeni Ücret Tanımla
                        </Button>
                    </Box>

                    {isMobile ? (
                        <Stack spacing={2}>
                            {ucretler.map((row: any) => (
                                <Card key={row.id} sx={{ borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                                            <Typography variant="body2" fontWeight={800} color="textSecondary">
                                                {row.baslangic_tarih} - {row.bitis_tarih === '31-12-2099' ? 'GÜNCEL' : row.bitis_tarih}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5}>
                                                <IconButton size="small" onClick={() => onEditUcret(row)} sx={{ bgcolor: 'grey.50' }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => onDeleteUcret(row.id)} sx={{ bgcolor: 'grey.50' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Stack>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}><UcretItem label="Saatlik" value={row.saat} /></Grid>
                                            <Grid item xs={6}><UcretItem label="Günlük" value={row.gun} /></Grid>
                                            <Grid item xs={6}><UcretItem label="Aylık" value={row.ay} /></Grid>
                                            <Grid item xs={6}><UcretItem label="Ekstra" value={row.ekstra} /></Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    ) : (
                        <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                                        <TableCell sx={{ color: 'white', fontWeight: 800 }}>Başlangıç</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 800 }}>Bitiş</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontWeight: 800 }}>Saatlik</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontWeight: 800 }}>Günlük</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontWeight: 800 }}>Aylık</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontWeight: 800 }}>Ekstra</TableCell>
                                        <TableCell align="right" sx={{ color: 'white', fontWeight: 800 }}>İşlem</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ucretler.map((row: any) => (
                                        <TableRow key={row.id} sx={{ '&:hover': { bgcolor: 'primary.light', '& .MuiTableCell-root': { color: 'primary.dark' } } }}>
                                            <TableCell sx={{ fontWeight: 600 }}>{row.baslangic_tarih}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{row.bitis_tarih === '31-12-2099' ? <Chip label="GÜNCEL" size="small" color="success" sx={{ fontWeight: 800, borderRadius: '4px', height: 20 }} /> : row.bitis_tarih}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{row.saat} ₺</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{row.gun} ₺</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{row.ay} ₺</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{row.ekstra} ₺</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => onEditUcret(row)}><EditIcon fontSize="small"/></IconButton>
                                                <IconButton size="small" color="error" onClick={() => onDeleteUcret(row.id)}><DeleteIcon fontSize="small"/></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}
        </Stack>
    );
};

const UcretItem = ({ label, value }: { label: string, value: number }) => (
    <Box>
        <Typography variant="caption" color="textSecondary" display="block" fontWeight={700}>{label}</Typography>
        <Typography variant="body1" fontWeight={800} color="primary.main">{value} ₺</Typography>
    </Box>
);

export default RollerVeUcretlerTab;
